import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import useLoyaltyInfo from 'common/components/hooks/loyalty/use-loyalty-info';
import { translate } from 'common/localization';
import { isEmpty } from 'underscore';
import { redirect } from 'common/util/bc.url.utils';
import SubscribeModal from 'common/components/banner/subscribe-modal';
import LoyaltyCouponModal from 'common/components/banner/loyalty-coupon-modal';
import LoyaltyCouponSimplifiedModal from 'common/components/banner/loyalty-coupon-simplified-modal';
import {
  sendNTFBannerClose,
  sendNTFBannerSubmitEvent,
  sendLoyaltyModalEvent,
  sendEvent,
} from 'common/util/gtm-helper';

const CouponBanner = (props) => {
  const [isCouponApplied, setIsCouponApplied] = useState(props.isCouponApplied);
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isShowingEmailField, setIsShowingEmailField] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({});
  const [loyaltyInfo] = useLoyaltyInfo();
  const isLoyaltyMember = loyaltyInfo &&
    loyaltyInfo.loyaltySubscribedSites &&
    loyaltyInfo.loyaltySubscribedSites.indexOf(BC.site.id) >= 0;
  const {
    claimCoupon,
    claimCouponByEmail,
    closeBanner,
    couponId,
    handleSubscribe,
    isAnimated,
    isEmailSubs,
    loyalty,
    rejectCoupon,
    restrictions,
    startChat,
    successRedirectUrl,
    sendCouponByEmail,
    image,
    experience,
    loyaltyCouponClaimRequest,
    couponClaimRequest,
    profile,
  } = props;
  const { cta, success } = isLoyaltyMember ? loyalty : props;
  const messaging = isCouponApplied ? success : cta;
  const listPagesRedirect = [
    'category',
    'search',
    'brand',
    'brand-home',
    'brand-plp',
    'activity',
    'rule',
  ];

  useEffect(() => {
    sendEvent({
      eventAction: 'modal open',
      eventCategory: 'ntf modal',
      eventLabel: '',
      event: 'customEvent',
    });
  }, []);

  const displayError = (newError = {}) => {
    setError({
      buttons: [{ title: translate('label.continue.shopping'), url: '#' }],
      description: translate('label.coupon.error.message'),
      title: `${translate('label.sorry')}!`,
      ...newError,
    });
  };
  const applyCoupon = () => {
    claimCoupon(couponId, true)
      .then((data) => {
        if (data.success) {
          setIsCouponApplied(true);
          setIsShowingEmailField(false);
          setError({});

          if (!isEmpty(successRedirectUrl)) {
            redirect(successRedirectUrl);
          }
        } else {
          displayError();
          setIsCouponApplied(true);
          setIsShowingEmailField(false);
        }
      })
      .fail(() => {
        displayError();
        setIsShowingEmailField(false);
      });
  };
  const handleCloseBanner = (redirectUrl) => {
    rejectCoupon(couponId, isCouponApplied);
    closeBanner();
    sendNTFBannerClose(messaging.title);

    if (
      !isEmpty(redirectUrl) &&
      listPagesRedirect.includes(BC.page.type) &&
      typeof redirectUrl === 'string' &&
      redirectUrl !== '#'
    ) {
      redirect(redirectUrl);
    }
  };
  const handleLoyaltyCloseBanner = () => {
    rejectCoupon(couponId, isCouponApplied);
    closeBanner();
  };
  const handleCloseDetails = () => {
    setIsShowingDetails(false);
  };
  const handleOpenDetails = () => {
    setIsShowingDetails(true);
  };
  const subscribeAndApply = (email, recaptchaResponse) => {
    handleSubscribe({ email, recaptchaResponse })
      .then(() => {
        sendNTFBannerSubmitEvent('email submit', messaging.title, {
          custom: { subscriptionEmail: email },
        });
        applyCoupon();
      })
      .fail(handleSubscribeError);
  };
  const subscribeAndSend = (email, recaptchaResponse) => {
    handleSubscribe({
      email,
      recaptchaResponse,
      couponId,
      sendCouponByEmail,
    })
      .then(() => {
        sendNTFBannerSubmitEvent('email submit', messaging.title, {
          custom: { subscriptionEmail: email },
        });
        setIsCouponApplied(true);
        setIsShowingEmailField(false);
        setError({});
        claimCouponByEmail(couponId, true);

        if (!isEmpty(successRedirectUrl)) {
          redirect(successRedirectUrl);
        }
      })
      .fail(handleSubscribeError);
  };
  const handleSubscribeError = (error) => {
    const errorMessage = {};
    let showEmailField = false;

    sendNTFBannerSubmitEvent(`submit failure|${error.reason}`, '');
    if (error.reason === 'invalidEmail') {
      errorMessage.description = translate('label.invalid.email.try.again');
      showEmailField = true;
      BC.pub('coupon.banner.email.error');
    } else if (error.reason === 'usedEmail') {
      errorMessage.title = translate('label.title.invalid.new.customers');
      errorMessage.description = translate(
        'label.discount.invalid.new.customers'
      );
    } else if (error.reason === 'invalidRecaptcha') {
      errorMessage.description = translate('label.invalid.recaptcha');
      showEmailField = true;
    }

    displayError(errorMessage);
    setIsShowingEmailField(showEmailField);
  };
  const claimLoyaltyCouponByEmail = (email, onCallback) => {
    onCallback();
    try {
      loyaltyCouponClaimRequest(email, couponId)
        .then(({ success, error, errorCode }) => {
          onCallback();
          if (success) {
            setIsCouponApplied(true);
          } else {
            const errorMessage = {
              title: translate('label.loyalty.coupon.error.server.title'),
              description: translate('label.loyalty.coupon.error.server.text'),
            };

            let isEmailInUse = false;

            switch (errorCode) {
              case 'alreadyApplied':
                errorMessage.title = translate(
                  'label.loyalty.error.coupon.applied'
                );
                errorMessage.description = translate(
                  'label.loyalty.error.coupon.applied.msj'
                );
                break;
              case 'loyaltyEmailUsed':
                errorMessage.title = translate(
                  'label.loyalty.coupon.emailInUse'
                );
                errorMessage.description = `${email} ${translate('label.loyalty.emailInUseMessage')}`;
                isEmailInUse = true;
                break;
              default:
                break;
            }

            sendLoyaltyModalEvent(
              'error',
              `unrecognized|${
                isEmailInUse ? translate('label.loyalty.emailInUseMessage') : error
              }`
            );
            setError(errorMessage);
          }
        })
        .fail(() => {
          setError({
            title: translate('label.loyalty.coupon.error.server.title'),
            description: translate('label.loyalty.coupon.error.server.text'),
          });
        });
    } catch {
      onCallback();
      setError({
        title: translate('label.loyalty.coupon.error.server.title'),
        description: translate('label.loyalty.coupon.error.server.text'),
      });
    }
  };
  const claimLoyaltyCoupon = () => {
    setIsLoading(true);
    setError({});
    couponClaimRequest(couponId)
      .done((data) => {
        if (data.invalidData) {
          if (data.errorMessage === 'label.coupon.error.already.applied') {
            setError({
              title: translate('label.loyalty.error.coupon.applied'),
              description: translate('label.loyalty.error.coupon.applied.msj'),
            });
          } else {
            setError({
              title: translate('label.loyalty.coupon.error.server.title'),
              description: translate('label.loyalty.coupon.error.server.text'),
            });
          }
        } else {
          sendLoyaltyModalEvent('discount applied click', 'success');
          setIsCouponApplied(true);
        }
      })
      .fail(() => {
        setError({
          title: translate('label.loyalty.coupon.error.server.title'),
          description: translate('label.loyalty.coupon.error.server.text'),
        });
      })
      .always(() => {
        setIsLoading(false);
      });
  };
  const buttonAction = isCouponApplied || !isEmpty(error) ? handleCloseBanner : applyCoupon;
  const subscribeAction = sendCouponByEmail ? subscribeAndSend : subscribeAndApply;
  const showEmailSub = isEmailSubs && isShowingEmailField && !isCouponApplied;
  const contentProps = showEmailSub ? { ...messaging } : { ...messaging, ...error };

  if (experience === 'loyalty') {
    return (
      <LoyaltyCouponModal
        {...props}
        closeBanner={handleLoyaltyCloseBanner}
        isLoading={isLoading}
        error={error}
        setError={setError}
        claimLoyaltyCouponByEmail={claimLoyaltyCouponByEmail}
        claimLoyaltyCoupon={claimLoyaltyCoupon}
        isCouponApplied={isCouponApplied}
        setIsCouponApplied={setIsCouponApplied}
        profile={profile}
      />
    );
  } else if (experience === 'loyalty-simplified') {
    return (
      <LoyaltyCouponSimplifiedModal
        {...props}
        closeBanner={handleLoyaltyCloseBanner}
        isLoading={isLoading}
        error={error}
        claimCoupon={claimLoyaltyCoupon}
        isCouponApplied={isCouponApplied}
        profile={profile}
      />
    );
  }

  return (
    <SubscribeModal
      isShowingDetails={isShowingDetails}
      couponAppliedSuccessfully={isCouponApplied}
      onRedeem={buttonAction}
      handleCloseDetails={handleCloseDetails}
      handleOpenDetails={handleOpenDetails}
      isEmailSubs={showEmailSub}
      onSubscribe={subscribeAction}
      error={error}
      image={image}
      {...contentProps}
      restrictions={restrictions}
      startChat={startChat}
      isAnimated={isAnimated}
      closeBanner={handleCloseBanner}
      type="coupon"
    />
  );
};

CouponBanner.propTypes = {
  claimCoupon: PropTypes.func.isRequired,
  claimCouponByEmail: PropTypes.func.isRequired,
  closeBanner: PropTypes.func,
  couponId: PropTypes.string.isRequired,
  cta: PropTypes.shape({
    buttons: PropTypes.array,
    description: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  experience: PropTypes.string.isRequired,
  handleSubscribe: PropTypes.func,
  isAnimated: PropTypes.bool,
  isCouponApplied: PropTypes.bool,
  isEmailSubs: PropTypes.bool.isRequired,
  loyalty: PropTypes.shape({
    cta: PropTypes.shape({
      buttons: PropTypes.array,
      description: PropTypes.string,
      title: PropTypes.string,
    }),
    success: PropTypes.shape({
      buttons: PropTypes.array,
      description: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
  rejectCoupon: PropTypes.func.isRequired,
  restrictions: PropTypes.shape({
    items: PropTypes.array.isRequired,
    cta: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  startChat: PropTypes.func,
  successRedirectUrl: PropTypes.string,
  success: PropTypes.shape({
    buttons: PropTypes.array.isRequired,
    description: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  loyaltyCouponClaimRequest: PropTypes.func.isRequired,
  couponClaimRequest: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    imagePath: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    login: PropTypes.string.isRequired,
  }).isRequired,
};

CouponBanner.defaultProps = {
  loyalty: {
    cta: {},
    success: {},
  },
  successRedirectUrl: '',
  cta: {
    buttons: [],
  },
};

export default CouponBanner;
